import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { buildAvatarImageSource } from '../../utils/buildAvatarImageSource';
import {
    resolveCollectionImageSrc,
    resolveCollectionVideoSrc,
} from '../../utils/mds/resolveCollectionPreviewSrc';
import { useGridImageOptimizer } from '../../utils/mds/useGridImageAlias';
import { BREAKPOINTS } from '../../utils/styles/breakpoints';
import { useIsCollectionPromoteInFeed } from '../../utils/useIsCollectionPromoteInFeed';
import { useTranslation } from '../../utils/useTranslation';
import { Skeleton } from '../feedback/Skeleton';
import { IconClock } from '../icon/IconClock';
import { IconDeclinedFilled } from '../icon/IconDeclinedFilled';
import { IconImage } from '../icon/IconImage';
import { IconKebab } from '../icon/IconKebab';
import { IconLink } from '../icon/IconLink';
import { IconStatistic } from '../icon/IconStatistic';
import { IconButton } from '../input/IconButton';
import { Box, BoxProps } from '../layout/Box';
import { DataTestId } from '../layout/DataTestIdWrapper';
import { Flex, FlexProps } from '../layout/Flex';
import { Stack } from '../layout/Stack';
import { Avatar } from '../media/Avatar';
import { Image } from '../media/Image';
import { MediaCover } from '../media/MediaCover';
import { Menu, MenuItem } from '../navigation/Menu';
import { Typography } from '../typography/Typography';
import { AVATAR_SIZE } from './constants';
import { CollectionStatus, CollectionUI } from './types';
import type { MDSAliases } from '../../utils/mds/getMdsUrl';

interface CollectionCardRootExtraProps {
    columnsCount?: number;
}

const CollectionCardRoot = styled(Flex)
    .withConfig({
        displayName: 'CollectionCardRoot',
        shouldForwardProp: (prop) => prop !== 'columnsCount',
    })
    .attrs<CollectionCardRootExtraProps, FlexProps & CollectionCardRootExtraProps>(
        ({ columnsCount = 3 }) => ({
            flexGrow: { xs: '1', sm: '0' },
            flexBasis: { xs: 'calc(50% - 8px)', md: `calc(100% / ${columnsCount} - 8px)` },
            maxWidth: { xs: '50%', md: `calc(100% / ${columnsCount})` },
            textDecoration: 'none',
        }),
    )``;

interface CollectionCardProps {
    collection: CollectionUI;
    isSaved: boolean;
    isOwner: boolean;
    onRemove: VoidFunction;
    onRemoveSaved: VoidFunction;
    onSave: VoidFunction;
    onEdit: VoidFunction;
    onShare: VoidFunction;
    onMove: VoidFunction;
    isOwnerDataHidden?: boolean;
    columnsCount?: number;
}

const estimateCollectionCardWidth = (columnsCount: number) => {
    if (typeof window === 'undefined') {
        return 180;
    }

    const isDesktop = window.innerWidth >= BREAKPOINTS.md;
    const containerWidth = window.innerWidth;

    return isDesktop ? containerWidth / columnsCount - 8 : containerWidth * 0.5 - 8;
};

const CollectionPreviewImage = ({
    preview,
    imageAlias,
}: {
    preview?: CollectionUI['previewItems'][number];
    imageAlias?: MDSAliases;
}) => {
    if (!preview) {
        return <IconImage fontSize="30px" fill="var(--color-primary)" />;
    }

    if (preview.type === 'image') {
        const { src, srcSet } = resolveCollectionImageSrc(preview.media, imageAlias);

        return <Image src={src} srcSet={srcSet} />;
    }

    if (preview.type === 'video' && preview.media[0]?.firstFrameUrl) {
        return (
            <Image src={resolveCollectionVideoSrc(preview.media[0].firstFrameUrl, imageAlias)} />
        );
    }

    return <IconImage fontSize="30px" fill="var(--color-primary)" />;
};

const CollectionMediaPreview = (props: { collection: CollectionUI; columnsCount?: number }) => {
    const { collection, columnsCount = 3 } = props;
    const { previewItems } = collection;
    const { getCompactAlias } = useGridImageOptimizer();

    const imageAlias = useMemo(
        () => getCompactAlias(estimateCollectionCardWidth(columnsCount)),
        [columnsCount, getCompactAlias],
    );

    if (collection.defaultCollection) {
        return (
            <MediaCover
                width="100%"
                ratio={1}
                borderRadius={1}
                border={{ xs: '1px solid var(--background-secondary)', sm: 'none' }}
            >
                <Stack
                    backgroundColor="var(--background-secondary)"
                    direction="row"
                    flexWrap="wrap"
                    spacing={1 / 8}
                    count={4}
                    renderItem={(index) => {
                        const preview = previewItems[index];

                        return (
                            <Flex
                                key={index}
                                width="50%"
                                height="50%"
                                flexBasis="calc(50% - 0.5px)"
                                alignItems="center"
                                justifyContent="center"
                                backgroundColor="var(--background-primary)"
                            >
                                <CollectionPreviewImage preview={preview} imageAlias={imageAlias} />
                            </Flex>
                        );
                    }}
                />
            </MediaCover>
        );
    }

    const preview = previewItems?.[0];

    return (
        <MediaCover
            width="100%"
            borderRadius={1}
            ratio={1}
            backgroundColor="var(--background-primary)"
            border={{ xs: '1px solid var(--background-secondary)', sm: 'none' }}
        >
            {preview ? (
                <CollectionPreviewImage preview={preview} imageAlias={imageAlias} />
            ) : (
                <Flex alignItems="center" justifyContent="center" width="60px" height="60px">
                    <IconImage fontSize="60px" fill="var(--color-primary)" />
                </Flex>
            )}
        </MediaCover>
    );
};

interface StatusProps extends BoxProps {
    collection: CollectionUI;
}

const Status = ({ collection }: StatusProps) => {
    const { status, isPublic } = collection;
    const promoteInFeed = useIsCollectionPromoteInFeed(collection);
    if (
        status !== CollectionStatus.DECLINED &&
        status !== CollectionStatus.MODERATION &&
        status !== CollectionStatus.LIMITED &&
        !(status === CollectionStatus.PUBLISHED && isPublic) &&
        !promoteInFeed
    ) {
        return <></>;
    }

    if (promoteInFeed) {
        return (
            <IconButton
                variant="filled"
                shape="rect"
                size="xs"
                backgroundColor="var(--accent-purple)"
            >
                <IconStatistic fontSize={16} />
            </IconButton>
        );
    }

    return (
        <IconButton
            variant="filled"
            shape="rect"
            size="xs"
            backgroundColor={
                status === CollectionStatus.DECLINED ? 'rgba(255, 91, 91, 1)' : undefined
            }
        >
            {status === CollectionStatus.MODERATION && <IconClock fontSize={16} />}
            {status === CollectionStatus.DECLINED && <IconDeclinedFilled fontSize={16} />}
            {(status === CollectionStatus.PUBLISHED || status === CollectionStatus.LIMITED) &&
            isPublic ? (
                <IconLink fontSize={16} />
            ) : null}
        </IconButton>
    );
};

const getStatusText = (collection: CollectionUI) => {
    const t = useTranslation();
    const promoteInFeed = useIsCollectionPromoteInFeed(collection);

    if (collection.defaultCollection) {
        return t('Все сохранённые посты');
    }

    if (promoteInFeed) {
        return t('Можно добавить в ленту');
    }

    if (collection.status === CollectionStatus.LIMITED) {
        return collection.isPublic ? t('Публичная коллекция') : t('Секретная коллекция');
    }

    switch (collection.status) {
        case CollectionStatus.DECLINED:
            return t('Коллекция отклонена');
        case CollectionStatus.MODERATION:
            return t('На модерации');
        case CollectionStatus.PUBLISHED:
            return t('Публичная коллекция');
        default:
            return 'Секретная коллекция';
    }
};

export const CollectionCard = (props: CollectionCardProps) => {
    const {
        collection,
        onRemove,
        onEdit,
        onShare,
        onMove,
        onRemoveSaved,
        onSave,
        isSaved,
        isOwner,
        isOwnerDataHidden,
        columnsCount,
    } = props;
    const t = useTranslation();

    const promoteInFeed = useIsCollectionPromoteInFeed(collection);
    const statusText = getStatusText(collection);

    return (
        <CollectionCardRoot
            as={Link}
            to={collection.url}
            onClick={onMove}
            dataTestId={DataTestId.COLLECTION_CARD_FROM_PROFILE}
            columnsCount={columnsCount}
        >
            <Box position="relative" width="100%">
                <CollectionMediaPreview collection={collection} columnsCount={columnsCount} />
                {!collection.defaultCollection && (
                    <>
                        <Menu
                            trigger={
                                <Box position="absolute" right={1} top={1}>
                                    <IconButton variant="filled" shape="rect" size="xs">
                                        <IconKebab
                                            fontSize={16}
                                            fill="#FFF"
                                            dataTestId={DataTestId.COLLECTION_KEBAB}
                                        />
                                    </IconButton>
                                </Box>
                            }
                            dataTestId={DataTestId.COLLECTION_MENU}
                        >
                            {isOwner ? (
                                <MenuItem onClick={onEdit}>
                                    <Typography noWrap dataTestId={DataTestId.EDIT_TEXT}>
                                        {t('Редактировать')}
                                    </Typography>
                                </MenuItem>
                            ) : undefined}
                            {collection.isPublic ? (
                                <MenuItem onClick={onShare}>
                                    <Typography noWrap>{t('Поделиться')}</Typography>
                                </MenuItem>
                            ) : undefined}
                            {isOwner ? (
                                <MenuItem onClick={onRemove}>
                                    <Typography
                                        color="error"
                                        noWrap
                                        dataTestId={DataTestId.DELETE_TEXT}
                                    >
                                        {t('Удалить')}
                                    </Typography>
                                </MenuItem>
                            ) : (
                                <MenuItem onClick={isSaved ? onRemoveSaved : onSave}>
                                    <Typography
                                        color={isSaved ? 'error' : undefined}
                                        noWrap
                                        dataTestId={
                                            isSaved
                                                ? DataTestId.REMOVE_SAVED_COLLECTION
                                                : DataTestId.SAVE_COLLECTION
                                        }
                                    >
                                        {t(isSaved ? 'Удалить из сохранённых' : 'Сохранить')}
                                    </Typography>
                                </MenuItem>
                            )}
                        </Menu>
                        {isOwner ? (
                            <Box position="absolute" left={1} top={1}>
                                <Status collection={collection} />
                            </Box>
                        ) : null}
                    </>
                )}
            </Box>
            <Typography
                pt={1}
                pb={isOwner ? undefined : 0.5}
                variant="body"
                weight="medium"
                dataTestId={DataTestId.COLLECTION_NAME}
            >
                {collection.displayName}
            </Typography>
            {isOwner ? (
                <Typography
                    variant="caption"
                    color={promoteInFeed ? 'var(--accent-purple)' : 'secondary'}
                    pb={1}
                >
                    {statusText}
                </Typography>
            ) : null}
            {!isOwner && !isOwnerDataHidden && (
                <Flex
                    direction="row"
                    alignItems="center"
                    cursor="pointer"
                    width="100%"
                    spacing={1}
                    pb={1}
                >
                    <Avatar
                        minWidth={AVATAR_SIZE}
                        width={AVATAR_SIZE}
                        height={AVATAR_SIZE}
                        title={collection.author?.displayName}
                        src={buildAvatarImageSource(collection.author?.avatarUrl)}
                        border="0.5px solid var(--divider)"
                    />
                    <Typography variant="caption" lines={1}>
                        {collection.author?.displayName || ''}
                    </Typography>
                </Flex>
            )}
        </CollectionCardRoot>
    );
};

export const CollectionCardSkeleton = ({ columnsCount = 3 }: { columnsCount?: number }) => (
    <CollectionCardRoot columnsCount={columnsCount}>
        <Skeleton variant="rect" width="100%" borderRadius={1.5} ratio={1} />
        <Box py={1}>
            <Skeleton variant="text" width="30%" height={20} />
        </Box>
    </CollectionCardRoot>
);
